import { useState, useEffect, useCallback } from 'react';

/**
 * sticky 헤더의 하단 위치를 측정하여 다음 sticky 요소의 top 값을 계산하는 훅.
 *
 * 대상 요소의 computed CSS `top` + `offsetHeight` = 바로 아래 sticky 요소가 사용할 top 값.
 * callback ref 패턴으로 요소가 늦게 마운트되어도(로딩 상태 후 렌더링 등) 정상 동작.
 * ResizeObserver로 크기 변화, MutationObserver로 inline style 변경을 자동 감지.
 */
export function useStickyOffset<T extends HTMLElement>() {
  const [el, setEl] = useState<T | null>(null);
  const [stickyTop, setStickyTop] = useState(0);

  const ref = useCallback((node: T | null) => {
    setEl(node);
  }, []);

  useEffect(() => {
    if (!el) return;

    const update = () => {
      const style = getComputedStyle(el);
      const top = parseFloat(style.top) || 0;
      const height = el.offsetHeight;
      setStickyTop(top + height);
    };

    update();

    // ResizeObserver: 크기 변화 감지
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    // MutationObserver: inline style 변경 감지 (React 상태로 top이 업데이트될 때)
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(el, { attributes: true, attributeFilter: ['style'] });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [el]);

  return { ref, stickyTop };
}
